import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "lib/utils";
import { type NavItem, type NavSection, isNavItemActive } from "../../navigation";

/* ══════════════════════════════════════════════════════════════════════════
   Sidebar navigation rows.

   Changes from the previous implementation:
   • Section headings. Fourteen ungrouped destinations is a list you read;
     five labelled groups is a map you scan.
   • The active row is marked with a rail *and* a weight change, so the state
     survives a colour-blind or low-contrast viewing.
   • Collapsed tooltips render in a portal — the old ones were absolutely
     positioned inside `overflow-y-auto` nav, so they were clipped.
   • Real focus-visible rings. Rows previously set `outline-none` with no
     replacement, leaving keyboard users with no visible position.
   ══════════════════════════════════════════════════════════════════════════ */

/** Tooltip for the collapsed rail — portalled so the scroll container can't clip it. */
const RailTooltip: React.FC<{ label: string; anchor: DOMRect | null }> = ({ label, anchor }) => {
    if (!anchor) return null;
    return createPortal(
        <div
            role="tooltip"
            className="fixed z-[95] pointer-events-none animate-fade-in"
            style={{ left: anchor.right + 10, top: anchor.top + anchor.height / 2 }}
        >
            <div className="-translate-y-1/2 rounded-lg bg-[#0b1120] text-white text-meta font-medium px-2.5 py-1.5 shadow-elevated border border-white/10 whitespace-nowrap">
                {label}
                <span
                    className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0b1120]"
                    aria-hidden="true"
                />
            </div>
        </div>,
        document.body
    );
};

export const SidebarLink: React.FC<{
    item: NavItem;
    collapsed: boolean;
    onNavigate: () => void;
    /** Indented child of a collapsible group */
    nested?: boolean;
}> = ({ item, collapsed, onNavigate, nested = false }) => {
    const location = useLocation();
    const ref = useRef<HTMLAnchorElement>(null);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const Icon = item.icon;
    const active = isNavItemActive(item, location.pathname);

    const showTooltip = () => {
        if (collapsed && ref.current) setRect(ref.current.getBoundingClientRect());
    };
    const hideTooltip = () => setRect(null);

    return (
        <>
            <NavLink
                ref={ref}
                to={item.path}
                onClick={onNavigate}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip}
                onBlur={hideTooltip}
                aria-current={active ? "page" : undefined}
                title={collapsed ? undefined : item.description}
                className={cn(
                    "relative group flex items-center rounded-xl w-full max-w-full min-w-0 touch-manipulation",
                    "transition-[background-color,color] duration-150 ease-ds",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111827]",
                    collapsed
                        ? "justify-center w-11 h-11 mx-auto"
                        : cn("gap-2.5 px-2.5 py-2 min-h-[40px]", nested && "pl-2"),
                    active
                        ? "bg-white/[0.09] text-white"
                        : "text-slate-400 hover:bg-white/[0.055] hover:text-slate-100"
                )}
            >
                {/* Active rail — anchored to the row, not the icon, so it reads
                    as "you are here in this list" rather than decoration. */}
                {active && !collapsed && (
                    <span
                        aria-hidden="true"
                        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600"
                    />
                )}

                <span
                    aria-hidden="true"
                    className={cn(
                        "flex-shrink-0 inline-flex items-center justify-center rounded-lg transition-colors duration-150",
                        collapsed ? "w-9 h-9" : "w-[26px] h-[26px]",
                        active
                            ? "bg-brand-500/25 text-brand-300"
                            : "text-slate-400 group-hover:text-slate-100"
                    )}
                >
                    <Icon className={collapsed ? "w-[18px] h-[18px]" : "w-[17px] h-[17px]"} strokeWidth={active ? 2.1 : 1.8} />
                </span>

                {!collapsed && (
                    <span
                        className={cn(
                            "truncate text-ui leading-snug flex-1 min-w-0",
                            active ? "font-semibold" : "font-medium"
                        )}
                    >
                        {item.label}
                    </span>
                )}

                {item.live && (
                    <span
                        className={cn(
                            "w-1.5 h-1.5 rounded-full bg-danger-solid animate-pulse flex-shrink-0",
                            collapsed ? "absolute top-1.5 right-1.5" : "ml-auto"
                        )}
                        aria-label="Live"
                    />
                )}
            </NavLink>

            {collapsed && <RailTooltip label={item.label} anchor={rect} />}
        </>
    );
};

/** Section heading. Collapsed, it degrades to a hairline so the rhythm survives. */
export const SidebarSectionLabel: React.FC<{ label?: string; collapsed: boolean }> = ({
    label,
    collapsed,
}) => {
    if (collapsed || !label) {
        return <div className="my-2 mx-2 border-t border-white/[0.06]" aria-hidden="true" />;
    }
    return (
        <p className="px-3 pt-4 pb-1.5 text-2xs font-bold uppercase tracking-[0.14em] text-slate-500 select-none">
            {label}
        </p>
    );
};

export const SidebarGroup: React.FC<{
    section: NavSection;
    collapsed: boolean;
    onNavigate: () => void;
}> = ({ section, collapsed, onNavigate }) => {
    const location = useLocation();
    const containsActive = section.items.some((item) => isNavItemActive(item, location.pathname));
    const [expanded, setExpanded] = useState(containsActive);

    // Navigating into the group opens it; we never force it shut, so a user
    // who opened it manually keeps it open while they browse.
    useEffect(() => {
        if (containsActive) setExpanded(true);
    }, [containsActive]);

    if (collapsed) {
        return (
            <div className="space-y-0.5">
                <SidebarSectionLabel collapsed={collapsed} />
                {section.items.map((item) => (
                    <SidebarLink key={item.id} item={item} collapsed onNavigate={onNavigate} />
                ))}
            </div>
        );
    }

    const groupId = `sidebar-group-${section.id}`;

    return (
        <div>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-controls={groupId}
                className={cn(
                    "w-full flex items-center gap-2.5 px-2.5 py-2 mt-3 rounded-xl text-ui font-semibold",
                    "transition-colors duration-150 ease-ds",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111827]",
                    containsActive ? "text-white bg-white/[0.055]" : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.055]"
                )}
            >
                <span className="truncate flex-1 text-left text-2xs font-bold uppercase tracking-[0.14em]">
                    {section.label}
                </span>
                <ChevronDown
                    className={cn("w-3.5 h-3.5 transition-transform duration-200", expanded && "rotate-180")}
                    aria-hidden="true"
                />
            </button>

            {expanded && (
                <div
                    id={groupId}
                    className="mt-0.5 ml-3 pl-2.5 border-l border-white/[0.07] space-y-0.5 flex flex-col items-stretch"
                >
                    {section.items.map((item) => (
                        <SidebarLink key={item.id} item={item} collapsed={false} onNavigate={onNavigate} nested />
                    ))}
                </div>
            )}
        </div>
    );
};
